'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Mail, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface EmailVerificationProps {
  email: string;
  userId?: string;
  onVerified: () => void;
}

export default function EmailVerification({ email, onVerified }: EmailVerificationProps) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Start cooldown timer on mount (code was already sent)
  useEffect(() => {
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const focusNext = (index: number) => {
    if (index < 5) inputRefs.current[index + 1]?.focus();
  };

  const focusPrev = (index: number) => {
    if (index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleChange = (index: number, value: string) => {
    const char = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = char;
    setDigits(newDigits);
    if (char) focusNext(index);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      } else {
        focusPrev(index);
      }
    } else if (e.key === 'ArrowLeft') {
      focusPrev(index);
    } else if (e.key === 'ArrowRight') {
      focusNext(index);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length !== 6) {
      toast.error('Bitte alle 6 Ziffern eingeben.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/verify-email-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), code }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || 'Falscher Code. Bitte erneut versuchen.');
        setDigits(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        return;
      }

      toast.success('✅ E-Mail-Adresse erfolgreich bestätigt!');
      onVerified();
    } catch {
      toast.error('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch('/api/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || 'Code konnte nicht erneut gesendet werden.');
        return;
      }

      toast.success('Neuer Code wurde gesendet!');
      setDigits(['', '', '', '', '', '']);
      setResendCooldown(60);
      inputRefs.current[0]?.focus();

      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      toast.error('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setResending(false);
    }
  };

  const isComplete = digits.every((d) => d !== '');

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6 text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(124,58,237,0.15)' }}
        >
          <Mail size={26} style={{ color: 'var(--neon-violet)' }} />
        </div>
        <h2 className="font-extrabold text-2xl mb-2">E-Mail bestätigen</h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Wir haben einen 6-stelligen Code an{' '}
          <span className="font-semibold" style={{ color: 'var(--foreground)' }}>
            {email}
          </span>{' '}
          gesendet. Bitte gib ihn unten ein.
        </p>
      </div>

      {/* Code inputs */}
      <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="w-11 h-14 text-center text-xl font-bold rounded-xl outline-none transition-all"
            style={{
              background: 'var(--input)',
              border: `2px solid ${digit ? 'var(--neon-violet)' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = digit ? 'var(--neon-violet)' : 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            autoFocus={i === 0}
          />
        ))}
      </div>

      {/* Verify button */}
      <button
        type="button"
        onClick={handleVerify}
        disabled={loading || !isComplete}
        className="w-full btn-primary flex items-center justify-center gap-2 py-3 font-bold text-sm rounded-xl disabled:opacity-60 disabled:cursor-not-allowed mb-4"
        style={{ minHeight: '46px' }}
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <>
            <CheckCircle2 size={16} />
            Code bestätigen
          </>
        )}
      </button>

      {/* Resend */}
      <div className="text-center">
        <p className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>
          Keinen Code erhalten?
        </p>
        {resendCooldown > 0 ? (
          <p className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
            Erneut senden in{' '}
            <span style={{ color: 'var(--neon-violet)' }}>{resendCooldown}s</span>
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-sm font-semibold flex items-center gap-1.5 mx-auto hover:opacity-80 transition-opacity disabled:opacity-50"
            style={{ color: 'var(--neon-violet)' }}
          >
            {resending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            Code erneut senden
          </button>
        )}
      </div>
    </div>
  );
}
